import React, { createContext, useContext, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiInfo, FiCheckCircle, FiAlertTriangle, FiXCircle, FiX } = FiIcons;

const NotificationContext = createContext();

export const useNotification = () => {
  return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((message, type = 'info', duration = 5000) => {
    const id = uuidv4();
    setNotifications(prev => [...prev, { id, message, type, duration }]);
    if (duration) {
      setTimeout(() => removeNotification(id), duration);
    }
  }, []);

  const removeNotification = id => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const icons = {
    info: <SafeIcon icon={FiInfo} className="w-6 h-6 text-blue-500" />,
    success: <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500" />,
    warning: <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-yellow-500" />,
    error: <SafeIcon icon={FiXCircle} className="w-6 h-6 text-red-500" />,
  };

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      {children}
      <div className="fixed top-5 right-5 z-[1000] flex flex-col items-end space-y-2">
        <AnimatePresence>
          {notifications.map(notification => (
            <motion.div
              key={notification.id}
              layout
              initial={{ opacity: 0, y: -50, scale: 0.3 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.5 }}
              transition={{ duration: 0.3 }}
              className={`relative w-full max-w-sm p-4 overflow-hidden rounded-lg shadow-lg bg-gray-800 border border-gray-700`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">{icons[notification.type]}</div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-white">{notification.message}</p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    onClick={() => removeNotification(notification.id)}
                    className="inline-flex text-gray-400 hover:text-gray-200 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};